/**
 * /ninety — My Day. Tab strip (owner, 9/23 redesign):
 *   Summary · To-Dos · Issues · Monthly Projects · Job Notes · Knowledge ·
 *   Processes · Calendar · Meetings   (9/24 sheet: Org Chart moved to Company)
 *
 * This is a VISUAL REFERENCE for the development team: To-Dos, Issues, Monthly
 * Projects, Job Notes, Processes and the Summary run on the fake in-memory store
 * in ~/components/myday/store.ts (edits work, reset on reload). Knowledge,
 * Calendar and Meetings are the existing screens, unchanged.
 *
 * Directory and /ninety/accountability (Org Chart) still resolve by URL.
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '~/lib/cn';
import { ME_ID, useMyDay } from '~/components/myday/store';
import {
  IconOverview, IconTodo, IconIssue, IconGem, IconNotes, IconBook, IconFolder, IconCalendar, IconUsers,
} from '~/components/myday/ui';

export const Route = createFileRoute('/_app/ninety')({
  component: NinetyLayout,
});

type BadgeKey = 'todos' | 'issues' | 'projects';

const TABS: { key: string; label: string; to: string; icon: ReactNode; badge?: BadgeKey }[] = [
  { key: 'summary',    label: 'Summary',          to: '/ninety',                icon: <IconOverview /> },
  { key: 'todos',      label: 'To-Dos',           to: '/ninety/todos',          icon: <IconTodo />, badge: 'todos' },
  { key: 'issues',     label: 'Issues',           to: '/ninety/issues',         icon: <IconIssue />, badge: 'issues' },
  { key: 'projects',   label: 'Monthly Projects', to: '/ninety/projects',       icon: <IconGem />, badge: 'projects' },
  { key: 'job-notes',  label: 'Job Notes',        to: '/ninety/job-notes',      icon: <IconNotes /> },
  { key: 'knowledge',  label: 'Knowledge',        to: '/ninety/knowledge',      icon: <IconBook /> },
  { key: 'processes',  label: 'Processes',        to: '/ninety/processes',      icon: <IconFolder /> },
  { key: 'calendar',   label: 'Calendar',         to: '/ninety/calendar',       icon: <IconCalendar /> },
  { key: 'meetings',   label: 'Meetings',         to: '/ninety/meetings',       icon: <IconUsers className="w-4 h-4" /> },
];

// Badge colors match the reference screens: blue for to-dos / projects, amber for issues.
const BADGE_STYLE: Record<BadgeKey, { bg: string; fg: string }> = {
  todos:    { bg: '#dbeafe', fg: '#1d4ed8' },
  issues:   { bg: '#fef3c7', fg: '#b45309' },
  projects: { bg: '#dbeafe', fg: '#1d4ed8' },
};

function NinetyLayout() {
  const { location } = useRouterState();
  const path = location.pathname.replace(/\/+$/, '');
  const todos = useMyDay((s) => s.todos);
  const issues = useMyDay((s) => s.issues);
  const projects = useMyDay((s) => s.projects);
  const counts: Record<BadgeKey, number> = {
    // Other people's private to-dos are never shown, so they don't count.
    todos: todos.filter((t) => !t.done && (!t.isPrivate || t.assigneeId === ME_ID)).length,
    issues: issues.filter((i) => !i.closed).length,
    projects: projects.filter((p) => p.status !== 'done').length,
  };

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }}>
      <div className="px-4 sm:px-6 pt-4">
        <h1 className="font-display" style={{
          fontSize: '40px', lineHeight: 1.1, fontWeight: 800, color: '#032e5b', margin: 0, letterSpacing: '0.3px',
        }}>My Day</h1>
        <nav className="mt-3 flex gap-1 overflow-x-auto rounded-xl border border-slate-200 p-1"
          style={{ background: '#f1f5f9', scrollbarWidth: 'none' }}>
          {TABS.map((t) => {
            const isActive = t.key === 'summary'
              ? path === '/ninety'
              : path === t.to || path.startsWith(t.to + '/');
            return (
              <Link key={t.key} to={t.to}
                className={cn(
                  'inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-[14px] font-semibold whitespace-nowrap no-underline transition-colors',
                  isActive ? 'bg-white shadow-sm text-blue-700' : 'text-slate-700 hover:bg-white/60',
                )}>
                <span className={isActive ? 'text-blue-600' : 'text-slate-500'}>{t.icon}</span>
                {t.label}
                {t.badge && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold inline-flex items-center justify-center"
                    style={{ background: BADGE_STYLE[t.badge].bg, color: BADGE_STYLE[t.badge].fg }}>
                    {counts[t.badge]}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="w-full">
        <Outlet />
      </div>
    </div>
  );
}
